import { useState } from 'react';
import { MagnifyingGlassIcon, ArrowsUpDownIcon, ArrowDownTrayIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const StockList = ({ items, loading, onEdit, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('itemName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterType, setFilterType] = useState('all');

  // Filter items
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.partNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || item.type === filterType;

    return matchesSearch && matchesType;
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle undefined values
    if (aValue === undefined) aValue = '';
    if (bValue === undefined) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Calculate statistics
  const totalItems = items.length;
  const totalValue = items.reduce((sum, item) => sum + (item.totalValue || 0), 0);
  const lowStockItems = items.filter(item => item.quantity <= item.reorderLevel).length;

  // Format date helper
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Export to CSV function
  const exportToCSV = () => {
    const headers = [
      'Item Name',
      'Part Number',
      'Type',
      'Quantity',
      'Reorder Level',
      'Location',
      'Unit Cost',
      'Total Value',
      'Description',
      'Date Added',
      'Last Updated',
      'Status'
    ];

    const csvData = sortedItems.map(item => [
      item.itemName || '',
      item.partNumber || '',
      item.type || '',
      item.quantity || 0,
      item.reorderLevel || 0,
      item.location || '',
      item.unitCost || 0,
      item.totalValue || 0,
      item.description || '',
      formatDate(item.dateAdded),
      formatDate(item.lastUpdated),
      item.status || 'active'
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistics Cards - Compact */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="ml-4">
              <dt className="text-xs font-semibold text-gray-500 uppercase">Total Items</dt>
              <dd className="text-2xl font-black text-gray-900">{totalItems}</dd>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <dt className="text-xs font-semibold text-gray-500 uppercase">Total Value</dt>
              <dd className="text-2xl font-black text-gray-900">₦{totalValue.toLocaleString('en-NG')}</dd>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 p-3 bg-gradient-to-br from-red-100 to-rose-100 rounded-xl">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-4">
              <dt className="text-xs font-semibold text-gray-500 uppercase">Low Stock</dt>
              <dd className="text-2xl font-black text-red-600">{lowStockItems}</dd>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search - Compact */}
      <div className="bg-white shadow-lg rounded-xl p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {/* Search */}
          <div className="sm:col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by item name, part number, or location..."
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="part">Parts Only</option>
              <option value="consumable">Consumables Only</option>
            </select>
          </div>

          {/* Export Button */}
          <button
            onClick={exportToCSV}
            className="inline-flex justify-center items-center px-4 py-2 rounded-xl shadow-md text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
            title="Export to CSV"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stock Table - Compact */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-500 to-cyan-600">
              <tr>
                <th
                  scope="col"
                  className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-blue-600"
                  onClick={() => toggleSort('itemName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Item Name</span>
                    {sortBy === 'itemName' && (
                      <ArrowsUpDownIcon className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-blue-600"
                  onClick={() => toggleSort('partNumber')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Part Number</span>
                    {sortBy === 'partNumber' && (
                      <ArrowsUpDownIcon className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Type
                </th>
                <th
                  scope="col"
                  className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-blue-600"
                  onClick={() => toggleSort('quantity')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Quantity</span>
                    {sortBy === 'quantity' && (
                      <ArrowsUpDownIcon className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Location
                </th>
                <th
                  scope="col"
                  className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-blue-600"
                  onClick={() => toggleSort('unitCost')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Unit Cost</span>
                    {sortBy === 'unitCost' && (
                      <ArrowsUpDownIcon className={`h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Total Value
                </th>
                <th scope="col" className="px-2 py-1.5 text-right text-xs font-bold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-2 py-8 text-center text-sm text-gray-500">
                    No stock items found
                  </td>
                </tr>
              ) : (
                sortedItems.map((item) => (
                  <tr key={item.id} className={`hover:bg-blue-50 transition-colors ${item.quantity <= item.reorderLevel ? 'bg-red-50' : ''}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="text-xs font-semibold text-gray-900">{item.itemName}</div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="text-xs text-gray-600">{item.partNumber}</div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${
                        item.type === 'part' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-xs font-semibold text-gray-900">{item.quantity}</div>
                        {item.quantity <= item.reorderLevel && (
                          <svg className="ml-1 h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-600">
                      {item.location}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-semibold text-gray-900">
                      ₦{item.unitCost?.toLocaleString('en-NG')}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-bold text-gray-900">
                      ₦{item.totalValue?.toLocaleString('en-NG')}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-right text-xs font-medium">
                      <button
                        onClick={() => onEdit(item)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => onDelete(item.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results Summary */}
      <div className="text-xs text-gray-500 text-center bg-white rounded-xl shadow-lg p-2">
        Showing {sortedItems.length} of {totalItems} items
      </div>
    </div>
  );
};

export default StockList;
